import { toast } from "react-toastify";
import { store } from "../../../../redux/store";
import {
  setAllCompleted,
  updateImage,
  deleteImage,
  replaceImage,
} from "../../../../redux/slice/sketchSlice";
import { setForceBookRefresh, setImageUploadStatus, setNewImageUploadStatus } from "../../../../redux/slice/stepSlice";
import { mediaEdit } from "../../../../services/mediaService";

export const processSketchJob = async ({
  images,
  uploadToBackend,
  batchId,
  BASE_URL,
  RUNPOD_API_KEY,
  MAX_POLL_LIMIT,
  onCoverReady,
  onImageComplete,
  onError = () => {},
  shouldRegenerate = false,
  bookId = null, // to know which book it belongs to
}) => {
  const { dispatch } = store;
  if (images.length === 0) {
    toast.error("Please upload at least one image to continue.");
    return;
  }

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
    });

  const submitJob = async (base64, apiData = {}) => {
    const res = await fetch(`${BASE_URL}/run`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${RUNPOD_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        input: {
          image: base64,
          width: 1600,
          height: 2200,
          bookId,
          batchId,
          isCover: false,
          apiImageId: apiData?.imageId || null,
          apiToken: localStorage.getItem('auth_token')
        },
      }),
    });
    const data = await res.json();
    return data.id;
  };

  const pollJob = async (jobId, imageIndex, isEditable = false) => {
    let attempts = 0;
    let storedAwsData = null;

    const poll = async (isEditable = false) => {
      if (attempts >= MAX_POLL_LIMIT) {
        /* dispatch(
          updateImage({
            index: imageIndex,
            data: { status: "FAILED" },
          })
        ); */
        
        // ✅ DELETE IMAGE ON MAX POLL LIMIT EXCEEDED
        const currentImages = images;
        if (currentImages[imageIndex]?.id) {
          dispatch(deleteImage(currentImages[imageIndex].id));
        }

        if (!!currentImages[imageIndex]?._id) {
          dispatch(setImageUploadStatus({ type: 'remove', id: currentImages[imageIndex]._id }))
        }
        
        // Setup new image upload status to false in case it is true
        if (!!currentImages[imageIndex]?.id) dispatch(setNewImageUploadStatus({ type: 'remove', id: currentImages[imageIndex]?.id }))
        
        onError()
        return;
      }
      
      const res = await fetch(`${BASE_URL}/stream/${jobId}`, {
        headers: { Authorization: `Bearer ${RUNPOD_API_KEY}` },
      });
      const data = await res.json();
      attempts++;

      // 👈 Capture AWS data whenever it appears (any status)
      if (!storedAwsData && Array.isArray(data.stream)) {
        const awsOutput = data.stream.find((s) => s.output?.aws_upload?.s3_key);
        if (awsOutput) {
          storedAwsData = awsOutput;
        }
      }

      /* dispatch(
        updateImage({
          index: imageIndex,
          data: {
            progress:
              data.status === "COMPLETED" ? 100 : Math.min(attempts * 2, 95),
            status: data.status,
          },
        })
      ); */

      if (data.status === "COMPLETED") {
        const stream = Array.isArray(data.stream) ? data.stream : [];
        const awsOutput = storedAwsData || stream.find((s) => s.output?.aws_upload?.s3_key);
        const s3Key = awsOutput?.output.aws_upload?.s3_key;
        const uploadMeta = awsOutput?.output;
        const currentImages = images;

        if (!s3Key || !uploadMeta) {
          toast.error('Something went wrong saving AI images, please try again.')
  
          // ✅ DELETE IMAGE WHEN S3 KEY OR UPLOAD META IS MISSING
          if (currentImages[imageIndex]?.id) {
            dispatch(deleteImage(currentImages[imageIndex].id));
          }

          if (!!currentImages[imageIndex]?._id) {
            dispatch(setImageUploadStatus({ type: 'remove', id: currentImages[imageIndex]._id }))
          }
          
          // Setup new image upload status to false in case it is true
          if (!!currentImages[imageIndex]?.id) dispatch(setNewImageUploadStatus({ type: 'remove', id: currentImages[imageIndex]?.id }))
          onError()
          return
        }

        const resultUrl = `https://memory-doodle-test-api.iosx.in/image?id=${encodeURIComponent(
          s3Key
        )}`;
        const originalUrl = `https://memory-doodle-test-api.iosx.in/image?id=${encodeURIComponent(
          uploadMeta?.aws_upload?.original_image_s3_key || ""
        )}`;
        /* dispatch(
          updateImage({
            index: imageIndex,
            data: {
              progress: 100,
              status: "COMPLETED",
              resultUrl,
              meta: uploadMeta,
            },
          })
        ); */

        const callableApi = !isEditable ? uploadToBackend : mediaEdit

        callableApi({
          AIUrl: s3Key,
          original_image_s3_key:
            uploadMeta.aws_upload?.original_image_s3_key || "",
          batchId,
          isCover: imageIndex === 0 && typeof onCoverReady === "function", // only true if new cover
          AIDimension: { width: 1600, height: 2200 },
          promptId: uploadMeta.prompt_id || "",
          AIinfo: uploadMeta,
          bookId,
          ...(!isEditable ? {} : { mediaId: currentImages[imageIndex]?._id ?? currentImages[imageIndex]?.id ?? null }),
          ...(!!isEditable && !!shouldRegenerate ? { regeneratedCount: (currentImages[imageIndex]?.regenerateCount || currentImages[imageIndex]?.regeneratedCount || 0) + 1 } : {})
        }).then(({ data }) => {
          dispatch(replaceImage({ ...data, prevId: images[imageIndex].id || images[imageIndex]._id }))
          if (typeof onImageComplete === 'function') onImageComplete(imageIndex);
        }).catch(err => onError()).finally(() => {
          if (!!currentImages[imageIndex]?._id) {
            dispatch(setImageUploadStatus({ type: 'remove', id: currentImages[imageIndex]._id }))
          }

          // Setup new image upload status to false in case it is true
          if (!!currentImages[imageIndex]?.id) dispatch(setNewImageUploadStatus({ type: 'remove', id: currentImages[imageIndex]?.id }))
        })

        dispatch(setForceBookRefresh(1))

        // ✅ Call onCoverReady if it's the first image (cover)
        if (imageIndex === 0 && typeof onCoverReady === "function") {
          onCoverReady({
            resultUrl,
            originalUrl,
          });
        }
        return;
      }

      if (data.status === "FAILED") {
        // ✅ DELETE IMAGE WHEN JOB STATUS IS FAILED
        const currentImages = images;
        if (currentImages[imageIndex]?.id) {
          dispatch(deleteImage(currentImages[imageIndex].id));
        }
        
        if (!!currentImages[imageIndex]?._id) {
          dispatch(setImageUploadStatus({ type: 'remove', id: currentImages[imageIndex]._id }))
        }

        // Setup new image upload status to false in case it is true
        if (!!currentImages[imageIndex]?.id) dispatch(setNewImageUploadStatus({ type: 'remove', id: currentImages[imageIndex]?.id }))
        onError()
        return;
      }

      await new Promise((res) => setTimeout(res, 2000));
      return poll(isEditable);
    };

    return poll(isEditable);
  };

  for (let i = 0; i < images.length; i++) {
    try {
      if (!!images[i]?._id) {
        dispatch(setImageUploadStatus({ type: 'add', id: images[i]._id }))
      } else if (!images[i]?.isEditable) {
        dispatch(setNewImageUploadStatus({ type: 'add', id: images[i].id }))
      }
      
      const base64 = await fileToBase64(images[i].file);
      const jobId = await submitJob(
        base64,
        {
          imageId: !!images[i]?.isEditable ? images[i]?.id : (
            images[i]?._id || null
          )
        }
      );

      dispatch(
        updateImage({
          index: i,
          data: { jobId, status: "SUBMITTED" },
        })
      );

      await pollJob(jobId, i, (!!images[i]?._id || !!images[i]?.isEditable));
    } catch (err) {
      console.error("Sketch Error:", err);
      toast.error('Failed to convert image to base64, please try again.')
      
      // ✅ DELETE IMAGE WHEN BASE64 CONVERSION OR JOB SUBMISSION FAILS
      const currentImages = images;
      if (currentImages[i]?.id) {
        dispatch(deleteImage(currentImages[i].id));
      }

      if (!!currentImages[i]?._id) {
        dispatch(setImageUploadStatus({ type: 'remove', id: currentImages[i]._id }))
      }

      // Setup new image upload status to false in case it is true
      dispatch(setNewImageUploadStatus(false))
      if (!!currentImages[i]?.id) dispatch(setNewImageUploadStatus({ type: 'remove', id: currentImages[i].id }))
      onError()
    }
  }

  const finalImages = store.getState().sketch.images;
  const allDone = finalImages.every((img) => img.status === "COMPLETED" || !!img?.AIUrl);
  dispatch(setAllCompleted(allDone));
};